import { CardHead, Card, when } from "./ui";
import type { VisitRecord, VisitSummary } from "./types";

/* Same rule as the calls table: a grid from `md` up, a stacked row below it, `minmax(0,1fr)`
 * so a long network name cannot push the row wider than its card. */
const COLS = "md:grid-cols-[130px_minmax(0,1.2fr)_minmax(0,1fr)_130px_50px_44px]";

const countries = (() => {
  try {
    return new Intl.DisplayNames(undefined, { type: "region" });
  } catch {
    return null;
  }
})();

function country(code: string | null): string {
  if (!code) return "";
  // Cloudflare's "T1" is Tor and "XX" is unknown - neither is a region DisplayNames knows.
  if (!/^[A-Z]{2}$/.test(code) || code === "T1" || code === "XX") return "";
  try {
    return countries?.of(code) ?? code;
  } catch {
    return code;
  }
}

export function where(v: Pick<VisitRecord, "city" | "region" | "country">): string {
  const parts = [v.city, v.region !== v.city ? v.region : null, country(v.country)];
  return parts.filter(Boolean).join(", ") || "Somewhere";
}

function stayed(seconds: number | null): string {
  if (seconds === null) return "–";
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/** "Previewed in Slack ×2 and iMessage · 1 unconfirmed (likely a link scanner) · 3 of yours" */
function footnote(summary: VisitSummary): string {
  const previews = Object.entries(summary.previews)
    .sort(([, a], [, b]) => b - a)
    .map(([who, n]) => (n > 1 ? `${who} ×${n}` : who));
  const parts = [
    previews.length ? `Previewed in ${previews.length > 1 ? `${previews.slice(0, -1).join(", ")} and ${previews.at(-1)}` : previews[0]}` : "",
    summary.unconfirmed ? `${summary.unconfirmed} unconfirmed (likely a link scanner)` : "",
    summary.internal ? `${summary.internal} of yours` : "",
  ];
  return parts.filter(Boolean).join(" · ");
}

export function VisitsCard({ visits, summary }: { visits: VisitRecord[]; summary: VisitSummary | null }) {
  const note = summary ? footnote(summary) : "";
  return (
    <Card>
      <CardHead
        title="Visits to this link"
        hint="People who opened it - not link previews, scanners, or you"
      />
      <div className={`hidden md:grid ${COLS} gap-4 px-5 py-2.5 text-[12px] text-muted border-b border-line-soft bg-paper-soft`}>
        <div>When</div><div>Where</div><div>Network</div><div>Device</div><div>Stayed</div><div>Rang</div>
      </div>
      {visits.length === 0 && (
        <div className="px-5 py-5 text-[13px] text-muted">Nobody has opened the link yet.</div>
      )}
      {visits.map((v) => {
        const place = where(v);
        return (
          <div key={v.id} className={`grid grid-cols-1 ${COLS} gap-x-4 gap-y-0.5 px-5 py-3 text-[13px] items-center border-b border-line-soft last:border-b-0`}>
            <div className="hidden md:block">
              {when(v.at)}
              {v.returning && <span className="block text-[12px] text-muted">again</span>}
            </div>
            <div className="min-w-0">
              {/* What the hidden columns say, on one line, so a phone loses the grid and not the facts. */}
              <div className="md:hidden text-[12px] text-muted truncate">
                {when(v.at)}{v.returning ? " · again" : ""} · {v.device} · {stayed(v.seconds)}{v.called_at ? " · rang" : ""}
              </div>
              <div className="truncate" title={v.timezone ?? undefined}>{place}</div>
            </div>
            <div className="min-w-0 truncate text-muted" title={v.org ?? undefined}>{v.org || "–"}</div>
            <div className="hidden md:block truncate text-muted">{v.device}</div>
            <div className="hidden md:block text-muted">{stayed(v.seconds)}</div>
            <div className="hidden md:block">{v.called_at ? <span className="text-ok" title={when(v.called_at)}>✓</span> : <span className="text-faint">–</span>}</div>
          </div>
        );
      })}
      {note && <div className="px-5 py-3 text-[12px] text-muted border-t border-line-soft bg-paper-soft">{note}</div>}
    </Card>
  );
}
